import React, { useMemo, useRef, useState } from "react";
import Swal from "sweetalert2";
import axiosInstance from "../../services/api_helper";
import config from "../../helpers/config";
import PromotionFormModal from "./PromotionFormModal";

const openPdfBlobInNewTab = (blob, targetWindow) => {
    const pdfBlob =
        blob.type === "application/pdf"
            ? blob
            : new Blob([blob], { type: "application/pdf" });
    const url = window.URL.createObjectURL(pdfBlob);

    if (targetWindow && !targetWindow.closed) {
        targetWindow.location.replace(url);
    } else {
        const openedWindow = window.open(url, "_blank");
        if (!openedWindow) {
            window.location.assign(url);
        }
    }

    setTimeout(() => {
        window.URL.revokeObjectURL(url);
    }, 60000);
};

const LabelGenerationModal = ({
    isOpen,
    toggle,
    selectedProducts = [],
    filters = {},
}) => {
    const [submitting, setSubmitting] = useState(false);
    const pendingWindowRef = useRef(null);

    const initialTickets = useMemo(
        () =>
            (selectedProducts || []).map((product, index) => ({
                articleId: product.id || "",
                articleCode: product.code || "",
                articleLibelle: product.libelle || "",
                prixOriginal: product.prixVenteTTC1 ?? "",
                prixPromo: product.prixVenteTTC1 ?? "",
                barcode: product.codeBarre || "",
                barcodeType: "CODE128",
                quantiteEtiquettes: 1,
                ordreAffichage: index + 1,
            })),
        [selectedProducts]
    );

    const handleSubmit = async (values) => {
        if (!pendingWindowRef.current) {
            pendingWindowRef.current = window.open("about:blank", "_blank");
            if (pendingWindowRef.current && !pendingWindowRef.current.closed) {
                pendingWindowRef.current.document.title = "Generation PDF...";
                pendingWindowRef.current.document.body.innerHTML =
                    "<div style='font-family: Arial, sans-serif; padding: 24px;'>Generation du PDF...</div>";
            }
        }

        try {
            setSubmitting(true);
            const tickets = values?.tickets || [];
            const payload = {
                scope: "SELECTED",
                labelFormat: "50x30",
                barcodeType: "CODE128",
                showPrice: false,
                showCode: true,
                layout: "SHEET",
                qtyPerArticle: 1,
                query: filters.query || "",
                categorieId: filters.categorieId || null,
                productIds: tickets.flatMap((ticket) =>
                    Array(Math.max(Number(ticket.quantiteEtiquettes) || 1, 1)).fill(
                        Number(ticket.articleId)
                    )
                ),
                tickets,
            };

            if (payload.productIds.length === 0) {
                if (pendingWindowRef.current) {
                    pendingWindowRef.current.close();
                    pendingWindowRef.current = null;
                }

                Swal.fire({
                    icon: "warning",
                    title: "Aucun article selectionne",
                    text: "Selectionnez au moins un article avant de generer les etiquettes.",
                });
                return;
            }

            const response = await axiosInstance.post(
                `${config.baseURL}/api/articles/barcodes/labels/pdf`,
                payload,
                { responseType: "blob" }
            );

            openPdfBlobInNewTab(response.data, pendingWindowRef.current);
            pendingWindowRef.current = null;

            Swal.fire({
                icon: "success",
                title: "PDF genere",
                text: "Le PDF des etiquettes a ete ouvert dans un nouvel onglet.",
                confirmButtonColor: "#198754",
            });

            toggle();
        } catch (error) {
            console.error(error);

            if (pendingWindowRef.current) {
                pendingWindowRef.current.close();
                pendingWindowRef.current = null;
            }

            Swal.fire({
                icon: "error",
                title: "Erreur",
                text: "Impossible de generer le PDF des etiquettes.",
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <PromotionFormModal
            isOpen={isOpen}
            toggle={toggle}
            mode="labels"
            title="Generer des etiquettes code-barres (PDF)"
            submitLabel="Generer PDF"
            initialTickets={initialTickets}
            onSubmit={handleSubmit}
            submitting={submitting}
        />
    );
};

export default LabelGenerationModal;
